import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DishManager from './DishManager';
import AllergyManager from './AllergyManager';
import WaiterManager from './WaiterManager';
import EventCreator from './EventCreator';
import { useSocket } from '../../services/socket';
import { useAuthStore } from '../../stores/useAuthStore';

interface Event {
  id: string;
  name: string;
  isActive: boolean;
  isPaused: boolean;
  currentBreak: string;
  currentBreakIndex: number;
  totalBreaks: number;
  startTime: string;
  totalTables: number;
  reserveTables?: number[];
}

interface KitchenTotal {
  dishId: string;
  dishName: string;
  total: number;
  remaining?: number;
  allergies: Array<{
    tableNumber: number;
    allergyNames: string[];
    waiterName?: string;
  }>;
}

interface WaiterStatus {
  id: string;
  name: string;
  isActive: boolean;
  assignedTables: number[];
  lastSeen: string;
  totalOrders: number;
}

interface LiveStats {
  totalOrders: number;
  ordersCompleted: number;
  ordersInProgress: number;
  averageOrderTime: number;
  totalAllergies: number;
}

interface EmergencyAlert {
  id: string;
  type: 'emergency' | 'alert' | 'message' | 'battery';
  title: string;
  message: string;
  isPinned: boolean;
  isRead: boolean;
  createdAt: string;
  createdBy: string;
}

interface TableStat {
  tableNumber: number;
  guestCount: number;
  waiterName: string;
}

type ViewMode = 'dashboard' | 'live-event' | 'dishes' | 'waiters' | 'allergies' | 'settings' | 'archive' | 'create-event' | 'saved-events' | 'edit-event';

interface SavedEvent {
  id: string;
  name: string;
  status: string;
  tableCount: number;
  scheduledFor: string | null;
  waiterCount: number;
  breaks: any[];
  createdAt: string;
}

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuthStore();

  const [activeEvent, setActiveEvent] = useState<Event | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentView, setCurrentView] = useState<ViewMode>('dashboard');
  const [kitchenTotals, setKitchenTotals] = useState<KitchenTotal[]>([]);
  const [waiterStatuses, setWaiterStatuses] = useState<WaiterStatus[]>([]);
  const [liveStats, setLiveStats] = useState<LiveStats>({
    totalOrders: 0,
    ordersCompleted: 0,
    ordersInProgress: 0,
    averageOrderTime: 0,
    totalAllergies: 0
  });
  const [currentTime, setCurrentTime] = useState(new Date());

  // Settings state
  const [newPin, setNewPin] = useState('');
  const [pinChangeStatus, setPinChangeStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [isChangingPin, setIsChangingPin] = useState(false);

  // Archive state
  const [archivedEvents, setArchivedEvents] = useState<any[]>([]);
  const [isLoadingArchive, setIsLoadingArchive] = useState(false);
  const [isDeletingEvent, setIsDeletingEvent] = useState(false);
  const [selectedEventSummary, setSelectedEventSummary] = useState<any>(null);
  const [isLoadingEventSummary, setIsLoadingEventSummary] = useState(false);

  // Saved events (drafts) state
  const [savedEvents, setSavedEvents] = useState<SavedEvent[]>([]);
  const [isLoadingSavedEvents, setIsLoadingSavedEvents] = useState(false);
  const [editEventId, setEditEventId] = useState<string | null>(null);
  const [isStartingEvent, setIsStartingEvent] = useState(false);
  const [startEventPins, setStartEventPins] = useState<Array<{ waiterId: string; name: string; pin: string }> | null>(null);

  // Emergency alerts state
  const [emergencyAlerts, setEmergencyAlerts] = useState<EmergencyAlert[]>([]);
  const [showEmergencyBanner, setShowEmergencyBanner] = useState(false);

  // Waiter management state
  const [waiterAction, setWaiterAction] = useState<{ type: 'disconnect' | 'reset-pin' | 'new-pin'; waiterId: string; waiterName: string; newPin?: string } | null>(null);
  const [isProcessingWaiter, setIsProcessingWaiter] = useState(false);

  // Table assignment state
  const [tableAssignment, setTableAssignment] = useState<{ tableNumber: number; waiterId: string } | null>(null);
  const [isAssigningTable, setIsAssigningTable] = useState(false);

  // Table management state (for reassign/unassign/reserve)
  const [tableAction, setTableAction] = useState<{ tableNumber: number; currentWaiter?: string; action?: 'reassign' | 'unassign' | 'reserve' } | null>(null);
  const [isProcessingTable, setIsProcessingTable] = useState(false);
  const [showAddTablesModal, setShowAddTablesModal] = useState(false);
  const [tablesToAdd, setTablesToAdd] = useState(1);

  // Table stats state
  const [tableStats, setTableStats] = useState<TableStat[]>([]);
  const [totalGuests, setTotalGuests] = useState(0);

  // Add waiter during live event state
  const [showAddWaiterModal, setShowAddWaiterModal] = useState(false);
  const [availableWaiters, setAvailableWaiters] = useState<{ id: string; name: string }[]>([]);
  const [selectedWaiterToAdd, setSelectedWaiterToAdd] = useState<string>('');
  const [selectedTablesForNewWaiter, setSelectedTablesForNewWaiter] = useState<number[]>([]);
  const [isAddingWaiter, setIsAddingWaiter] = useState(false);
  const [newWaiterPin, setNewWaiterPin] = useState<{ waiterName: string; pin: string } | null>(null);

  // Messaging state
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [messageTarget, setMessageTarget] = useState<'all' | 'waiters' | 'kitchen' | 'specific'>('all');
  const [specificWaiterId, setSpecificWaiterId] = useState('');
  const [isSendingMessage, setIsSendingMessage] = useState(false);

  const { socket, isConnected, isAuthenticated, joinRoom, emit } = useSocket();

  // Load archived events when entering archive view
  useEffect(() => {
    if (currentView === 'archive') {
      loadArchivedEvents();
    }
  }, [currentView]);

  // Load saved events when entering saved-events view
  useEffect(() => {
    if (currentView === 'saved-events') {
      loadSavedEvents();
    }
  }, [currentView]);

  // Load emergency alerts from server
  const loadEmergencies = async () => {
    try {
      const response = await fetch('/api/v1/notifications/emergencies');
      const data = await response.json();
      if (data.success && data.emergencies) {
        setEmergencyAlerts(data.emergencies);
        if (data.emergencies.length > 0) {
          setShowEmergencyBanner(true);
        }
      }
    } catch (error) {
      console.error('Failed to load emergencies:', error);
    }
  };

  // Load kitchen totals and waiter statuses via HTTP
  const loadAdminData = async () => {
    try {
      const [totalsRes, waitersRes, tableStatsRes] = await Promise.all([
        fetch('/api/v1/orders/kitchen/totals'),
        fetch('/api/v1/waiters'),
        fetch('/api/v1/orders/tables/stats')
      ]);

      const totalsData = await totalsRes.json();
      const waitersData = await waitersRes.json();
      const tableStatsData = await tableStatsRes.json();

      if (totalsData.success && totalsData.totals) {
        setKitchenTotals(totalsData.totals);
        // Calculate live stats from totals
        const totalOrders = totalsData.totals.reduce((sum: number, d: KitchenTotal) => sum + d.total, 0);
        const totalAllergies = totalsData.totals.reduce((sum: number, d: KitchenTotal) => sum + d.allergies.length, 0);
        setLiveStats(prev => ({
          ...prev,
          totalOrders,
          ordersInProgress: totalOrders,
          totalAllergies
        }));
      }

      if (waitersData.success && waitersData.waiters) {
        // Format waiter data for dashboard - only include waiters assigned to active event
        const formattedWaiters = waitersData.waiters
          .filter((w: any) => w.currentEvent)
          .map((w: any) => ({
            id: w.id,
            name: w.name,
            isActive: w.currentEvent?.isOnline || false,
            assignedTables: w.currentEvent?.assignedTables || [],
            lastSeen: w.currentEvent?.lastSeenAt || new Date().toISOString(),
            totalOrders: 0
          }));
        setWaiterStatuses(formattedWaiters);
      }

      if (tableStatsData.success) {
        setTableStats(tableStatsData.tables || []);
        setTotalGuests(tableStatsData.totalGuests || 0);
      }
    } catch (error) {
      console.error('Failed to load admin data:', error);
    }
  };

  // Navigate to live event with fresh data
  const goToLiveEvent = async () => {
    // Reload all data to ensure fresh state
    await Promise.all([
      loadActiveEvent(),
      loadAdminData()
    ]);
    setCurrentView('live-event');
  };

  useEffect(() => {
    const clockInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    loadActiveEvent();
    loadEmergencies(); // Load pending emergencies on mount
    loadAdminData(); // Load admin data via HTTP on mount

    return () => clearInterval(clockInterval);
  }, []);

  useEffect(() => {
    if (socket && isConnected && isAuthenticated) {
      joinRoom('admin');

      // Notification and emergency alert handler
      const handleNotification = (notification: EmergencyAlert) => {
        if (notification.type === 'emergency' || notification.type === 'battery') {
          setEmergencyAlerts(prev => [notification, ...prev.filter(a => a.id !== notification.id)]);
          setShowEmergencyBanner(true);
          // Play alert sound
          try {
            const audio = new Audio('/alert.mp3');
            audio.volume = 0.5;
            audio.play().catch(() => {});
          } catch {}
          // Vibrate if supported
          if (navigator.vibrate) {
            navigator.vibrate([200, 100, 200]);
          }
        }
      };

      socket.on('notification', handleNotification);
      socket.on('emergency-alert', handleNotification);

      // Listen for emergency status changes
      socket.on('emergency-read', (data: { id: string; isRead: boolean }) => {
        setEmergencyAlerts(prev => prev.map(a => a.id === data.id ? { ...a, isRead: data.isRead } : a));
      });
      socket.on('emergency-cleared', (alertId: string) => {
        setEmergencyAlerts(prev => {
          const updated = prev.filter(a => a.id !== alertId);
          if (updated.length === 0) {
            setShowEmergencyBanner(false);
          }
          return updated;
        });
      });

      // Always register these listeners - they should work regardless of activeEvent state
      socket.on('event-updated', (event: Event) => {
        setActiveEvent(event);
      });

      socket.on('event-started', () => {
        // Reload active event when a new event starts (might be from another admin)
        loadActiveEvent();
      });

      socket.on('kitchen-totals-updated', (totals: KitchenTotal[]) => {
        setKitchenTotals(totals);
        // Update live stats
        const totalOrders = totals.reduce((sum, d) => sum + d.total, 0);
        const totalAllergies = totals.reduce((sum, d) => sum + d.allergies.length, 0);
        setLiveStats(prev => ({
          ...prev,
          totalOrders,
          ordersInProgress: totalOrders,
          totalAllergies
        }));
      });

      socket.on('waiter-status-updated', (statuses: WaiterStatus[]) => {
        setWaiterStatuses(statuses);
      });

      socket.on('live-stats-updated', (stats: LiveStats) => {
        setLiveStats(stats);
      });

      socket.on('table-stats-updated', (data: { tables: TableStat[]; totalGuests: number }) => {
        setTableStats(data.tables);
        setTotalGuests(data.totalGuests);
      });

      // Request admin data via socket
      emit('request-admin-data');

      // Also refresh via HTTP to ensure data is current
      loadAdminData();

      return () => {
        socket.off('notification');
        socket.off('emergency-alert');
        socket.off('emergency-read');
        socket.off('emergency-cleared');
        socket.off('event-updated');
        socket.off('event-started');
        socket.off('kitchen-totals-updated');
        socket.off('waiter-status-updated');
        socket.off('live-stats-updated');
        socket.off('table-stats-updated');
      };
    }
  }, [socket, isConnected, isAuthenticated]);

  const loadActiveEvent = async () => {
    try {
      const response = await fetch('/api/v1/events/active');
      const data = await response.json();

      if (data.success && data.event) {
        setActiveEvent(data.event);
      } else {
        setActiveEvent(null);
      }
    } catch (error) {
      console.error('Failed to load active event:', error);
      setActiveEvent(null);
    } finally {
      setIsLoading(false);
    }
  };

  const loadArchivedEvents = async () => {
    setIsLoadingArchive(true);
    try {
      const response = await fetch('/api/v1/events?status=ended');
      const data = await response.json();

      if (data.success && data.events) {
        setArchivedEvents(data.events);
      }
    } catch (error) {
      console.error('Failed to load archived events:', error);
    } finally {
      setIsLoadingArchive(false);
    }
  };

  const loadSavedEvents = async () => {
    setIsLoadingSavedEvents(true);
    try {
      const response = await fetch('/api/v1/events?status=draft');
      const data = await response.json();

      if (data.success && data.events) {
        setSavedEvents(data.events);
      }
    } catch (error) {
      console.error('Failed to load saved events:', error);
    } finally {
      setIsLoadingSavedEvents(false);
    }
  };

  const handleStartEvent = async (eventId: string) => {
    setIsStartingEvent(true);
    try {
      const response = await fetch(`/api/v1/events/${eventId}/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      const data = await response.json();

      if (data.success) {
        // Show PINs if waiters were assigned
        if (data.waiterPins && data.waiterPins.length > 0) {
          setStartEventPins(data.waiterPins);
        } else {
          // No waiters - go directly to live event
          await Promise.all([
            loadActiveEvent(),
            loadAdminData()
          ]);
          setCurrentView('live-event');
        }
      } else {
        alert(data.error || 'שגיאה בהפעלת האירוע');
      }
    } catch (error) {
      console.error('Failed to start event:', error);
      alert('שגיאת תקשורת');
    } finally {
      setIsStartingEvent(false);
    }
  };

  const handleDeleteDraftEvent = async (eventId: string) => {
    if (!confirm('האם אתה בטוח שברצונך למחוק את האירוע? פעולה זו לא ניתנת לביטול.')) {
      return;
    }

    try {
      const response = await fetch(`/api/v1/events/${eventId}`, {
        method: 'DELETE'
      });
      const data = await response.json();

      if (data.success) {
        // Remove from local state
        setSavedEvents(prev => prev.filter(e => e.id !== eventId));
      } else {
        alert(data.error || 'שגיאה במחיקת האירוע');
      }
    } catch (error) {
      console.error('Failed to delete event:', error);
      alert('שגיאת תקשורת');
    }
  };

  const handleEditEvent = (eventId: string) => {
    setEditEventId(eventId);
    setCurrentView('edit-event');
  };

  const handleDeleteEvent = async (eventId: string) => {
    if (!confirm('האם אתה בטוח שברצונך למחוק את האירוע? פעולה זו לא ניתנת לביטול.')) {
      return;
    }

    setIsDeletingEvent(true);
    try {
      const response = await fetch(`/api/v1/events/${eventId}`, {
        method: 'DELETE'
      });
      const data = await response.json();

      if (data.success) {
        // Remove from local state
        setArchivedEvents(prev => prev.filter(e => e.id !== eventId));
      } else {
        alert(data.error || 'שגיאה במחיקת האירוע');
      }
    } catch (error) {
      console.error('Failed to delete event:', error);
      alert('שגיאת תקשורת');
    } finally {
      setIsDeletingEvent(false);
    }
  };

  const loadEventSummary = async (eventId: string) => {
    setIsLoadingEventSummary(true);
    try {
      const response = await fetch(`/api/v1/events/${eventId}/summary`);
      const data = await response.json();
      if (data.success && data.summary) {
        setSelectedEventSummary(data.summary);
      } else {
        alert(data.error || 'שגיאה בטעינת פרטי האירוע');
      }
    } catch (error) {
      console.error('Failed to load event summary:', error);
      alert('שגיאת תקשורת');
    } finally {
      setIsLoadingEventSummary(false);
    }
  };

  const handleEndEvent = async () => {
    if (!activeEvent) return;
    if (!confirm('האם אתה בטוח שברצונך לסיים את האירוע? פעולה זו לא ניתנת לביטול.')) return;

    try {
      const response = await fetch(`/api/v1/events/${activeEvent.id}/end`, {
        method: 'POST'
      });
      const data = await response.json();
      if (data.success) {
        setActiveEvent(null);
        emit('end-event', { eventId: activeEvent.id });
      }
    } catch (error) {
      console.error('Failed to end event:', error);
    }
  };

  const handleNextBreak = async () => {
    if (!activeEvent) return;
    try {
      const response = await fetch(`/api/v1/events/${activeEvent.id}/next-break`, {
        method: 'POST'
      });
      const data = await response.json();
      if (data.success && data.event) {
        setActiveEvent(data.event);
        emit('break-changed', { eventId: activeEvent.id, break: data.event.currentBreak });
        // Refresh kitchen totals for the new break
        loadAdminData();
      }
    } catch (error) {
      console.error('Failed to advance break:', error);
    }
  };

  const handlePrevBreak = async () => {
    if (!activeEvent) return;
    try {
      const response = await fetch(`/api/v1/events/${activeEvent.id}/prev-break`, {
        method: 'POST'
      });
      const data = await response.json();
      if (data.success && data.event) {
        setActiveEvent(data.event);
        emit('break-changed', { eventId: activeEvent.id, break: data.event.currentBreak });
        // Refresh kitchen totals for the new break
        loadAdminData();
      }
    } catch (error) {
      console.error('Failed to go to previous break:', error);
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('he-IL', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatTimeElapsed = (startTime: string) => {
    const elapsed = Date.now() - new Date(startTime).getTime();
    const hours = Math.floor(elapsed / (1000 * 60 * 60));
    const minutes = Math.floor((elapsed % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}:${minutes.toString().padStart(2, '0')}`;
  };

  const handleAlertAction = async (alertId: string, action: 'handled' | 'clear') => {
    try {
      if (action === 'handled') {
        // Mark as read - update local state to show as read
        setEmergencyAlerts(prev =>
          prev.map(a => a.id === alertId ? { ...a, isRead: true } : a)
        );
        await fetch(`/api/v1/notifications/${alertId}/handled`, { method: 'POST' });
      } else {
        // Clear - remove from local state
        setEmergencyAlerts(prev => {
          const updated = prev.filter(a => a.id !== alertId);
          if (updated.length === 0) {
            setShowEmergencyBanner(false);
          }
          return updated;
        });
        await fetch(`/api/v1/notifications/${alertId}`, { method: 'DELETE' });
      }
    } catch (error) {
      console.error('Failed to handle alert:', error);
      // Reload emergencies to sync state
      loadEmergencies();
    }
  };

  // Legacy function for banner dismiss
  const dismissAlert = async (alertId: string) => {
    await handleAlertAction(alertId, 'handled');
  };

  const handleWaiterDisconnect = async (waiterId: string) => {
    setIsProcessingWaiter(true);
    try {
      const response = await fetch(`/api/v1/waiters/${waiterId}/disconnect`, {
        method: 'POST'
      });
      const data = await response.json();
      if (data.success) {
        setWaiterAction(null);
      } else {
        alert(data.error || 'שגיאה בניתוק המלצר');
      }
    } catch (error) {
      console.error('Failed to disconnect waiter:', error);
      alert('שגיאת תקשורת');
    } finally {
      setIsProcessingWaiter(false);
    }
  };

  const handleWaiterResetPin = async (waiterId: string, waiterName: string) => {
    setIsProcessingWaiter(true);
    try {
      const response = await fetch(`/api/v1/waiters/${waiterId}/reset-pin`, {
        method: 'POST'
      });
      const data = await response.json();
      if (data.success && data.pin) {
        setWaiterAction({ type: 'new-pin', waiterId, waiterName, newPin: data.pin });
      } else {
        alert(data.error || 'שגיאה באיפוס PIN');
      }
    } catch (error) {
      console.error('Failed to reset waiter PIN:', error);
      alert('שגיאת תקשורת');
    } finally {
      setIsProcessingWaiter(false);
    }
  };

  // Load available waiters (not assigned to current event)
  const loadAvailableWaiters = async () => {
    try {
      const response = await fetch('/api/v1/waiters');
      const data = await response.json();
      if (data.success && data.waiters) {
        // Filter waiters without current event assignment
        const available = data.waiters.filter((w: any) => !w.currentEvent);
        setAvailableWaiters(available.map((w: any) => ({ id: w.id, name: w.name })));
      }
    } catch (error) {
      console.error('Failed to load available waiters:', error);
    }
  };

  const handleAddWaiterToEvent = async () => {
    if (!selectedWaiterToAdd || !activeEvent) return;

    setIsAddingWaiter(true);
    try {
      const response = await fetch(`/api/v1/waiters/${selectedWaiterToAdd}/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          eventId: activeEvent.id,
          assignedTables: selectedTablesForNewWaiter
        })
      });
      const data = await response.json();
      if (data.success) {
        const waiter = availableWaiters.find(w => w.id === selectedWaiterToAdd);
        setNewWaiterPin({ waiterName: waiter?.name || '', pin: data.pin });
        setShowAddWaiterModal(false);
        setSelectedWaiterToAdd('');
        setSelectedTablesForNewWaiter([]);
        loadAdminData(); // Refresh waiter list
      } else {
        alert(data.error || 'שגיאה בהוספת מלצר');
      }
    } catch (error) {
      console.error('Failed to add waiter:', error);
      alert('שגיאת תקשורת');
    } finally {
      setIsAddingWaiter(false);
    }
  };

  const handleAssignTable = async (isReserved: boolean = false) => {
    if (!tableAssignment || !activeEvent) return;

    setIsAssigningTable(true);
    try {
      const endpoint = isReserved
        ? `/api/v1/events/${activeEvent.id}/reserve-table`
        : `/api/v1/events/${activeEvent.id}/assign-table`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          waiterId: tableAssignment.waiterId,
          tableNumber: tableAssignment.tableNumber
        })
      });
      const data = await response.json();
      if (data.success) {
        setTableAssignment(null);
        loadAdminData(); // Refresh waiter data
        // Update activeEvent with new reserveTables (for both assign and reserve)
        if (data.reserveTables !== undefined) {
          setActiveEvent(prev => prev ? { ...prev, reserveTables: data.reserveTables } : prev);
        }
        if (isReserved) {
          alert(data.message || 'השולחן הוקצה כשולחן רזרבה');
        }
      } else {
        alert(data.error || 'שגיאה בהקצאת שולחן');
      }
    } catch (error) {
      console.error('Failed to assign table:', error);
      alert('שגיאת תקשורת');
    } finally {
      setIsAssigningTable(false);
    }
  };

  // Calculate unassigned tables (excluding reserve tables)
  const getUnassignedTables = () => {
    if (!activeEvent) return [];
    const allTables = Array.from({ length: activeEvent.totalTables }, (_, i) => i + 1);
    const assignedTables = new Set(waiterStatuses.flatMap(w => w.assignedTables));
    const reserveTables = new Set(activeEvent.reserveTables || []);
    return allTables.filter(t => !assignedTables.has(t) && !reserveTables.has(t));
  };

  // Handle unassign table
  const handleUnassignTable = async (tableNumber: number) => {
    if (!activeEvent) return;
    setIsProcessingTable(true);
    try {
      const response = await fetch(`/api/v1/events/${activeEvent.id}/unassign-table`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tableNumber })
      });
      const data = await response.json();
      if (data.success) {
        setTableAction(null);
        loadAdminData();
      } else {
        alert(data.error || 'שגיאה בשחרור שולחן');
      }
    } catch (error) {
      console.error('Failed to unassign table:', error);
      alert('שגיאת תקשורת');
    } finally {
      setIsProcessingTable(false);
    }
  };

  // Handle add tables
  const handleAddTables = async () => {
    if (!activeEvent || tablesToAdd < 1) return;
    setIsProcessingTable(true);
    try {
      const response = await fetch(`/api/v1/events/${activeEvent.id}/add-tables`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ count: tablesToAdd })
      });
      if (!response.ok) {
        const data = await response.json();
        alert(data.error || 'שגיאה בהוספת שולחנות');
        return;
      }
      const data = await response.json();
      setShowAddTablesModal(false);
      setTablesToAdd(1);
      if (data.tableCount) {
        setActiveEvent(prev => prev ? { ...prev, totalTables: data.tableCount } : prev);
      }
    } catch (error) {
      console.error('Failed to add tables:', error);
      alert('שגיאת תקשורת');
    } finally {
      setIsProcessingTable(false);
    }
  };

  // Handle add reserve table
  const handleAddReserveTable = async (tableNumber: number) => {
    if (!activeEvent) return;
    setIsProcessingTable(true);
    try {
      const response = await fetch(`/api/v1/events/${activeEvent.id}/add-reserve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tableNumber })
      });
      const data = await response.json();
      if (data.success) {
        setTableAction(null);
        setActiveEvent(prev => prev ? { ...prev, reserveTables: data.reserveTables } : prev);
        loadAdminData();
      } else {
        alert(data.error || 'שגיאה בהוספת שולחן לרזרבה');
      }
    } catch (error) {
      console.error('Failed to add reserve table:', error);
      alert('שגיאת תקשורת');
    } finally {
      setIsProcessingTable(false);
    }
  };

  // Verify no active event before navigating to create-event view
  const handleCreateEventClick = async () => {
    if (activeEvent) return;

    // Re-verify with server before allowing
    try {
      const response = await fetch('/api/v1/events/active');
      const data = await response.json();

      if (data.success && data.event) {
        // An event exists, update state and don't navigate
        setActiveEvent(data.event);
        alert('לא ניתן ליצור אירוע חדש - קיים אירוע פעיל');
      } else {
        // No active event, safe to create
        setCurrentView('create-event');
      }
    } catch (error) {
      console.error('Failed to verify active event:', error);
      // If we can't verify, allow navigation (backend will reject if needed)
      setCurrentView('create-event');
    }
  };

  const WaiterActionModal = () => {
    if (!waiterAction) return null;

    if (waiterAction.type === 'new-pin') {
      return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-sm w-full">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 text-center">
              PIN חדש למלצר
            </h3>
            <p className="text-center text-gray-600 dark:text-gray-300 mb-4">
              {waiterAction.waiterName}
            </p>
            <div className="bg-blue-100 dark:bg-blue-900 rounded-lg p-6 mb-4">
              <div className="text-4xl font-mono font-bold text-blue-700 dark:text-blue-300 text-center tracking-widest">
                {waiterAction.newPin}
              </div>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 text-center mb-4">
              המלצר צריך להתחבר מחדש עם הקוד החדש
            </p>
            <button
              onClick={() => setWaiterAction(null)}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 rounded-lg transition-colors"
            >
              סגור
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-sm w-full">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 text-center">
            {waiterAction.type === 'disconnect' ? 'ניתוק מלצר' : 'איפוס PIN'}
          </h3>
          <p className="text-center text-gray-600 dark:text-gray-300 mb-6">
            {waiterAction.type === 'disconnect'
              ? `האם לנתק את ${waiterAction.waiterName}?`
              : `האם לאפס PIN עבור ${waiterAction.waiterName}?`
            }
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => setWaiterAction(null)}
              disabled={isProcessingWaiter}
              className="flex-1 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 text-gray-800 dark:text-white font-medium py-2 rounded-lg transition-colors"
            >
              ביטול
            </button>
            <button
              onClick={() => {
                if (waiterAction.type === 'disconnect') {
                  handleWaiterDisconnect(waiterAction.waiterId);
                } else {
                  handleWaiterResetPin(waiterAction.waiterId, waiterAction.waiterName);
                }
              }}
              disabled={isProcessingWaiter}
              className={`flex-1 font-medium py-2 rounded-lg transition-colors flex items-center justify-center gap-2 ${
                waiterAction.type === 'disconnect'
                  ? 'bg-red-600 hover:bg-red-700 text-white'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              {isProcessingWaiter && (
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
              )}
              {waiterAction.type === 'disconnect' ? 'נתק' : 'אפס'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const TableAssignmentModal = () => {
    if (!tableAssignment) return null;

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 text-center">
            הקצאת שולחן {tableAssignment.tableNumber}
          </h3>
          <p className="text-center text-gray-600 dark:text-gray-300 mb-4">
            בחר מלצר לשולחן זה
          </p>
          <div className="space-y-2 mb-4 max-h-60 overflow-y-auto">
            {waiterStatuses.map(waiter => (
              <button
                key={waiter.id}
                onClick={() => setTableAssignment(prev => prev ? { ...prev, waiterId: waiter.id } : null)}
                className={`w-full p-3 rounded-lg border text-right transition-colors ${
                  tableAssignment.waiterId === waiter.id
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/50'
                    : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${waiter.isActive ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                    <span className="font-medium text-gray-900 dark:text-white">{waiter.name}</span>
                  </div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {waiter.assignedTables.length > 0 ? `שולחנות: ${waiter.assignedTables.join(', ')}` : 'ללא שולחנות'}
                  </span>
                </div>
              </button>
            ))}
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setTableAssignment(null)}
              disabled={isAssigningTable}
              className="flex-1 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 text-gray-800 dark:text-white font-medium py-2 rounded-lg transition-colors"
            >
              ביטול
            </button>
            <button
              onClick={() => handleAssignTable(false)}
              disabled={isAssigningTable || !tableAssignment.waiterId}
              className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium py-2 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {isAssigningTable && (
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
              )}
              הקצה
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Table action modal (reassign, unassign, reserve)
  const TableActionModal = () => {
    if (!tableAction) return null;

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 text-center">
            שולחן {tableAction.tableNumber}
          </h3>
          {tableAction.currentWaiter && (
            <p className="text-center text-gray-600 dark:text-gray-300 mb-4">
              מוקצה ל: {tableAction.currentWaiter}
            </p>
          )}
          <div className="space-y-3">
            {/* Reassign to another waiter */}
            <button
              onClick={() => {
                setTableAction(null);
                setTableAssignment({ tableNumber: tableAction.tableNumber, waiterId: '' });
              }}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
              </svg>
              העבר למלצר אחר
            </button>

            {/* Unassign (make free) */}
            <button
              onClick={() => handleUnassignTable(tableAction.tableNumber)}
              disabled={isProcessingTable}
              className="w-full bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-400 text-white font-medium py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {isProcessingTable ? (
                <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
              )}
              שחרר שולחן (ללא מלצר)
            </button>

            {/* Add to reserve */}
            {!activeEvent?.reserveTables?.includes(tableAction.tableNumber) && (
              <button
                onClick={() => handleAddReserveTable(tableAction.tableNumber)}
                disabled={isProcessingTable}
                className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white font-medium py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                {isProcessingTable ? (
                  <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                ) : (
                  <span className="text-lg">⭐</span>
                )}
                הוסף לרזרבה
              </button>
            )}

            {/* Cancel */}
            <button
              onClick={() => setTableAction(null)}
              className="w-full bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 text-gray-800 dark:text-white font-medium py-3 rounded-lg transition-colors"
            >
              ביטול
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Add tables modal
  const AddTablesModal = () => {
    if (!showAddTablesModal) return null;

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-sm w-full">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 text-center">
            הוסף שולחנות
          </h3>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              כמות שולחנות להוספה
            </label>
            <input
              type="number"
              min="1"
              max="50"
              value={tablesToAdd}
              onChange={(e) => setTablesToAdd(Math.max(1, parseInt(e.target.value) || 1))}
              className="w-full p-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-center text-xl font-bold"
            />
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-4">
            סה״כ יהיו: {(activeEvent?.totalTables || 0) + tablesToAdd} שולחנות
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => {
                setShowAddTablesModal(false);
                setTablesToAdd(1);
              }}
              className="flex-1 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 text-gray-800 dark:text-white font-medium py-2 rounded-lg transition-colors"
            >
              ביטול
            </button>
            <button
              onClick={handleAddTables}
              disabled={isProcessingTable}
              className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-medium py-2 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {isProcessingTable && (
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
              )}
              הוסף
            </button>
          </div>
        </div>
      </div>
    );
  };

  const AddWaiterModal = () => {
    if (!showAddWaiterModal) return null;

    const unassignedTables = getUnassignedTables();

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 text-center">
            הוסף מלצר לאירוע
          </h3>

          {/* Waiter Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              בחר מלצר
            </label>
            {availableWaiters.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                אין מלצרים זמינים להוספה.
                <br />
                <span className="text-xs">כל המלצרים כבר משובצים באירוע או שאין מלצרים במערכת.</span>
              </p>
            ) : (
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {availableWaiters.map(waiter => (
                  <button
                    key={waiter.id}
                    onClick={() => setSelectedWaiterToAdd(waiter.id)}
                    className={`w-full p-3 rounded-lg border text-right transition-colors ${
                      selectedWaiterToAdd === waiter.id
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/50'
                        : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <span className="font-medium text-gray-900 dark:text-white">{waiter.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Table Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              בחר שולחנות - אופציונלי ({selectedTablesForNewWaiter.length} נבחרו)
            </label>
            {unassignedTables.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                כל השולחנות כבר מוקצים למלצרים.
              </p>
            ) : (
              <div className="grid grid-cols-5 gap-2">
                {unassignedTables.map(tableNum => (
                  <button
                    key={tableNum}
                    onClick={() => {
                      setSelectedTablesForNewWaiter(prev =>
                        prev.includes(tableNum)
                          ? prev.filter(t => t !== tableNum)
                          : [...prev, tableNum]
                      );
                    }}
                    className={`py-2 rounded-lg text-sm font-bold transition-colors ${
                      selectedTablesForNewWaiter.includes(tableNum)
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    {tableNum}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={() => {
                setShowAddWaiterModal(false);
                setSelectedWaiterToAdd('');
                setSelectedTablesForNewWaiter([]);
              }}
              disabled={isAddingWaiter}
              className="flex-1 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 text-gray-800 dark:text-white font-medium py-2 rounded-lg transition-colors"
            >
              ביטול
            </button>
            <button
              onClick={handleAddWaiterToEvent}
              disabled={isAddingWaiter || !selectedWaiterToAdd}
              className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium py-2 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {isAddingWaiter && (
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
              )}
              הוסף מלצר
            </button>
          </div>
        </div>
      </div>
    );
  };

  const NewWaiterPinModal = () => {
    if (!newWaiterPin) return null;

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-sm w-full">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 text-center">
            מלצר נוסף בהצלחה!
          </h3>
          <p className="text-center text-gray-600 dark:text-gray-300 mb-4">
            {newWaiterPin.waiterName}
          </p>
          <div className="bg-green-100 dark:bg-green-900 rounded-lg p-6 mb-4">
            <div className="text-sm text-green-700 dark:text-green-300 text-center mb-2">קוד PIN:</div>
            <div className="text-4xl font-mono font-bold text-green-700 dark:text-green-300 text-center tracking-widest">
              {newWaiterPin.pin}
            </div>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 text-center mb-4">
            המלצר יכול להתחבר עם קוד זה
          </p>
          <button
            onClick={() => setNewWaiterPin(null)}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 rounded-lg transition-colors"
          >
            סגור
          </button>
        </div>
      </div>
    );
  };

  const EmergencyAlertsBanner = () => {
    if (!showEmergencyBanner || emergencyAlerts.length === 0) return null;

    return (
      <div className="fixed top-0 left-0 right-0 z-50 bg-red-600 text-white shadow-lg animate-pulse">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-2xl">🚨</span>
              <div>
                <div className="font-bold">התראת חירום!</div>
                <div className="text-sm">
                  {emergencyAlerts[0]?.message || 'קריאה דחופה מהמלצר'}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {emergencyAlerts.length > 1 && (
                <span className="bg-white/20 px-2 py-1 rounded text-sm">
                  +{emergencyAlerts.length - 1} נוספות
                </span>
              )}
              <button
                onClick={goToLiveEvent}
                className="bg-white text-red-600 px-4 py-2 rounded-lg font-bold hover:bg-red-100 transition-colors"
              >
                צפה
              </button>
              <button
                onClick={() => {
                  if (emergencyAlerts[0]) dismissAlert(emergencyAlerts[0].id);
                }}
                className="bg-white/20 hover:bg-white/30 p-2 rounded-lg transition-colors"
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleSendMessage = async () => {
    if (!messageText.trim()) return;
    if (messageTarget === 'specific' && !specificWaiterId) return;

    setIsSendingMessage(true);
    try {
      const response = await fetch('/api/v1/notifications', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: messageText,
          targetType: messageTarget,
          targetId: messageTarget === 'specific' ? specificWaiterId : undefined
        })
      });
      const data = await response.json();
      if (data.success) {
        setShowMessageModal(false);
        setMessageText('');
        setMessageTarget('all');
        setSpecificWaiterId('');
        alert('ההודעה נשלחה בהצלחה');
      } else {
        alert(data.error || 'שגיאה בשליחת ההודעה');
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      alert('שגיאת תקשורת');
    } finally {
      setIsSendingMessage(false);
    }
  };

  const handlePinChange = async () => {
    if (newPin.length !== 4 || !/^\d{4}$/.test(newPin)) {
      setPinChangeStatus({ type: 'error', message: 'קוד PIN חייב להיות 4 ספרות' });
      return;
    }

    setIsChangingPin(true);
    setPinChangeStatus(null);

    try {
      const response = await fetch('/api/v1/auth/manager/set-pin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ newPin }),
      });

      const data = await response.json();

      if (data.success) {
        setPinChangeStatus({ type: 'success', message: 'קוד PIN עודכן בהצלחה' });
        setNewPin('');
      } else {
        setPinChangeStatus({ type: 'error', message: data.error || 'שגיאה בעדכון הקוד' });
      }
    } catch (error) {
      console.error('Failed to change PIN:', error);
      setPinChangeStatus({ type: 'error', message: 'שגיאת תקשורת' });
    } finally {
      setIsChangingPin(false);
    }
  };

  const MenuView = () => (
    <div className="max-w-4xl mx-auto p-6">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
          ברוכים הבאים למערכת Giggo
        </h2>
        <p className="text-gray-600 dark:text-gray-300">
          {activeEvent ? 'יש אירוע פעיל. בחר פעולה:' : 'אין אירוע פעיל כרגע. בחר פעולה מהאפשרויות הבאות:'}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Go to Live Event - only show if there's an active event */}
        {activeEvent && (
          <div
            onClick={goToLiveEvent}
            className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer col-span-full md:col-span-2 lg:col-span-3"
          >
            <div className="flex items-center justify-between text-white">
              <div>
                <h3 className="text-xl font-bold mb-1">
                  {activeEvent.name}
                </h3>
                <p className="text-green-100">
                  {activeEvent.currentBreak} • פעיל
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-lg font-medium">כניסה לאירוע</span>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </div>
            </div>
          </div>
        )}

        <div
          onClick={handleCreateEventClick}
          className={`bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 transition-shadow ${
            activeEvent
              ? 'opacity-50 cursor-not-allowed'
              : 'hover:shadow-lg cursor-pointer'
          }`}
        >
          <div className="text-center">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
              activeEvent
                ? 'bg-gray-200 dark:bg-gray-700'
                : 'bg-blue-100 dark:bg-blue-900'
            }`}>
              <svg className={`w-8 h-8 ${
                activeEvent
                  ? 'text-gray-400 dark:text-gray-500'
                  : 'text-blue-600 dark:text-blue-400'
              }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              צור אירוע חדש
            </h3>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              {activeEvent
                ? 'לא ניתן ליצור אירוע חדש כשאירוע פעיל'
                : 'התחל אירוע חדש עם הגדרות מותאמות אישית'
              }
            </p>
          </div>
        </div>

        <div
          onClick={() => setCurrentView('saved-events')}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer"
        >
          <div className="text-center">
            <div className="bg-purple-100 dark:bg-purple-900 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              אירועים שמורים
            </h3>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              הצג והפעל אירועים שנשמרו
            </p>
          </div>
        </div>

        <div
          onClick={() => setCurrentView('archive')}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer"
        >
          <div className="text-center">
            <div className="bg-gray-100 dark:bg-gray-700 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              צפה בארכיון
            </h3>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              עיין באירועים קודמים ובדוחות
            </p>
          </div>
        </div>

        <div
          onClick={() => setCurrentView('dishes')}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer"
        >
          <div className="text-center">
            <div className="bg-green-100 dark:bg-green-900 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              נהל מנות
            </h3>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              הוסף, ערוך או מחק מנות מהמאגר
            </p>
          </div>
        </div>

        <div
          onClick={() => setCurrentView('allergies')}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer"
        >
          <div className="text-center">
            <div className="bg-yellow-100 dark:bg-yellow-900 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              נהל אלרגיות
            </h3>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              הגדר רשימת אלרגיות עבור המנות
            </p>
          </div>
        </div>

        <div
          onClick={() => setCurrentView('waiters')}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer"
        >
          <div className="text-center">
            <div className="bg-purple-100 dark:bg-purple-900 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              נהל מלצרים
            </h3>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              הוסף והגדר פרופילים של מלצרים
            </p>
          </div>
        </div>

        <div
          onClick={() => setCurrentView('settings')}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer"
        >
          <div className="text-center">
            <div className="bg-gray-100 dark:bg-gray-700 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              הגדרות
            </h3>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              הגדרות כלליות ושינוי קוד PIN
            </p>
          </div>
        </div>

        {/* Logout Button */}
        <div
          onClick={() => {
            logout();
            navigate('/');
          }}
          className="bg-red-50 dark:bg-red-900/20 rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer border border-red-200 dark:border-red-800"
        >
          <div className="text-center">
            <div className="bg-red-100 dark:bg-red-900 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-red-700 dark:text-red-300 mb-2">
              יציאה
            </h3>
            <p className="text-red-600 dark:text-red-400 text-sm">
              התנתק מהמערכת
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const ActiveEventView = () => (
    <div className="max-w-7xl mx-auto p-6">
      {/* Back to Menu */}
      <button
        onClick={() => setCurrentView('dashboard')}
        className="mb-4 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white flex items-center gap-2"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        חזרה לתפריט
      </button>

      {/* Event Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6">
        <div className="flex justify-between items-center flex-wrap gap-4">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              {activeEvent?.name}
            </h2>
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full ml-2 bg-green-500"></div>
                <span className="text-lg font-medium text-green-600 dark:text-green-400">
                  פעיל
                </span>
              </div>
              <span className="text-blue-600 dark:text-blue-400 text-lg font-medium bg-blue-100 dark:bg-blue-900 px-3 py-1 rounded-full">
                {activeEvent?.currentBreak}
              </span>
              <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'} animate-pulse`}></div>
              <span className={`text-sm ${isConnected ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                {isConnected ? 'מחובר' : 'מנותק'}
              </span>
            </div>
          </div>

          <div className="text-left">
            <div className="text-4xl font-mono text-blue-600 dark:text-blue-400 mb-2">
              {formatTime(currentTime)}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              זמן מתחילת האירוע: {activeEvent && formatTimeElapsed(activeEvent.startTime)}
            </div>
          </div>
        </div>

        {/* Event Controls */}
        <div className="flex flex-wrap gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
          {/* Show "מנה קודמת" only if there's a previous break */}
          {activeEvent && activeEvent.currentBreakIndex > 0 && (
            <button
              onClick={handlePrevBreak}
              className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7M19 19l-7-7 7-7" />
              </svg>
              מנה קודמת
            </button>
          )}

          {/* Show "מנה הבאה" only if there's a next break */}
          {activeEvent && activeEvent.currentBreakIndex < activeEvent.totalBreaks - 1 && (
            <button
              onClick={handleNextBreak}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
              </svg>
              מנה הבאה
            </button>
          )}

          <button
            onClick={() => setShowMessageModal(true)}
            className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
            שלח הודעה
          </button>

          <button
            onClick={handleEndEvent}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10h6v4H9V10z" />
            </svg>
            סיים אירוע
          </button>
        </div>
      </div>

      {/* Live Stats Summary */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 text-center">
          <div className="text-3xl font-bold text-blue-600 dark:text-blue-400 mb-1">
            {liveStats.totalOrders}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">סה״כ הזמנות</div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 text-center">
          <div className="text-3xl font-bold text-red-600 dark:text-red-400 mb-1">
            {liveStats.totalAllergies}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">אלרגיות</div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 text-center">
          <div className="text-3xl font-bold text-purple-600 dark:text-purple-400 mb-1">
            {waiterStatuses.filter(w => w.isActive).length}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">מלצרים פעילים</div>
        </div>
      </div>

      {/* Unassigned Tables Warning */}
      {(() => {
        const unassignedTables = getUnassignedTables();
        if (unassignedTables.length === 0) return null;

        return (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg shadow-md p-4 mb-6">
            <h3 className="text-lg font-semibold text-yellow-700 dark:text-yellow-300 mb-3 flex items-center gap-2">
              <span className="text-xl">⚠️</span>
              שולחנות ללא מלצר ({unassignedTables.length})
            </h3>
            <div className="flex flex-wrap gap-2">
              {unassignedTables.map(tableNum => (
                <button
                  key={tableNum}
                  onClick={() => setTableAssignment({ tableNumber: tableNum, waiterId: '' })}
                  className="bg-white dark:bg-gray-800 hover:bg-yellow-100 dark:hover:bg-yellow-900/50 border border-yellow-300 dark:border-yellow-700 rounded-lg px-4 py-2 transition-colors"
                >
                  <span className="text-yellow-700 dark:text-yellow-300 font-bold">
                    שולחן {tableNum}
                  </span>
                  <span className="text-xs text-yellow-600 dark:text-yellow-400 block">
                    לחץ להקצאה
                  </span>
                </button>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Reserved Tables Section */}
      {activeEvent?.reserveTables && activeEvent.reserveTables.length > 0 && (
        <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg shadow-md p-4 mb-6">
          <h3 className="text-lg font-semibold text-purple-700 dark:text-purple-300 mb-3 flex items-center gap-2">
            <span className="text-xl">⭐</span>
            שולחנות רזרבה ({activeEvent.reserveTables.length})
          </h3>
          <div className="flex flex-wrap gap-2">
            {activeEvent.reserveTables.sort((a, b) => a - b).map(tableNum => (
              <button
                key={tableNum}
                onClick={() => setTableAssignment({ tableNumber: tableNum, waiterId: '' })}
                className="bg-white dark:bg-gray-800 hover:bg-purple-100 dark:hover:bg-purple-900/50 border border-purple-300 dark:border-purple-700 rounded-lg px-4 py-2 transition-colors"
              >
                <span className="text-purple-700 dark:text-purple-300 font-bold">
                  שולחן {tableNum}
                </span>
                <span className="text-xs text-purple-600 dark:text-purple-400 block">
                  לחץ להקצאה
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Emergency Alerts Section */}
      {emergencyAlerts.length > 0 && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg shadow-md p-4 mb-6">
          <h3 className="text-lg font-semibold text-red-700 dark:text-red-300 mb-3 flex items-center gap-2">
            <span className="text-xl">🚨</span>
            התראות פעילות ({emergencyAlerts.length})
          </h3>
          <div className="space-y-2">
            {emergencyAlerts.map((alert) => (
              <div
                key={alert.id}
                className={`flex items-center justify-between rounded-lg p-3 border ${
                  alert.isRead
                    ? 'bg-gray-100 dark:bg-gray-700/50 border-gray-300 dark:border-gray-600 opacity-70'
                    : 'bg-white dark:bg-gray-800 border-red-200 dark:border-red-700'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{alert.type === 'battery' ? '🔋' : '📞'}</span>
                  <div>
                    <div className={`font-medium ${alert.isRead ? 'text-gray-600 dark:text-gray-400' : 'text-gray-900 dark:text-white'}`}>
                      {alert.title}
                      {alert.isRead && <span className="mr-2 text-green-600 dark:text-green-400 text-xs">(טופל ✓)</span>}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">{alert.message}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-500">
                      {new Date(alert.createdAt).toLocaleTimeString('he-IL')}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  {!alert.isRead && (
                    <button
                      onClick={() => handleAlertAction(alert.id, 'handled')}
                      className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-lg text-sm transition-colors"
                    >
                      טופל
                    </button>
                  )}
                  <button
                    onClick={() => handleAlertAction(alert.id, 'clear')}
                    className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded-lg text-sm transition-colors"
                  >
                    נקה
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tables & Guest Count Summary */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 mb-6">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            שולחנות וסועדים
            <span className="bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-2 py-1 rounded-full text-sm font-bold mr-2">
              סה״כ: {totalGuests} סועדים | {activeEvent?.totalTables || 0} שולחנות
            </span>
          </h3>
          <button
            onClick={() => setShowAddTablesModal(true)}
            className="bg-green-600 hover:bg-green-700 text-white text-sm px-3 py-1 rounded-lg transition-colors flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            הוסף שולחנות
          </button>
        </div>
        {tableStats.length === 0 ? (
          <div className="text-center py-4 text-gray-500 dark:text-gray-400">
            אין נתוני שולחנות עדיין
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {tableStats.map((table) => (
              <button
                key={table.tableNumber}
                onClick={() => setTableAction({ tableNumber: table.tableNumber, currentWaiter: table.waiterName })}
                className="bg-gray-50 dark:bg-gray-700 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg p-3 text-center border border-gray-200 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500 transition-colors cursor-pointer"
              >
                <div className="text-lg font-bold text-gray-900 dark:text-white">
                  שולחן {table.tableNumber}
                </div>
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {table.guestCount}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {table.waiterName}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Allergies by Table - Summary */}
      {(() => {
        // Calculate unique tables with allergies and waiter names
        const tablesWithAllergies = new Map<number, { allergies: string[]; waiterName: string }>();
        kitchenTotals.forEach(dish => {
          dish.allergies.forEach(allergy => {
            const existing = tablesWithAllergies.get(allergy.tableNumber);
            if (existing) {
              tablesWithAllergies.set(allergy.tableNumber, {
                allergies: [...new Set([...existing.allergies, ...allergy.allergyNames])],
                waiterName: allergy.waiterName || existing.waiterName
              });
            } else {
              tablesWithAllergies.set(allergy.tableNumber, {
                allergies: allergy.allergyNames,
                waiterName: allergy.waiterName || ''
              });
            }
          });
        });

        if (tablesWithAllergies.size === 0) return null;

        return (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg shadow-md p-4 mb-6">
            <h3 className="text-lg font-semibold text-red-700 dark:text-red-300 mb-3 flex items-center gap-2">
              <span className="text-xl">⚠️</span>
              אלרגיות לפי שולחן ({tablesWithAllergies.size} שולחנות)
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {Array.from(tablesWithAllergies.entries())
                .sort((a, b) => a[0] - b[0])
                .map(([tableNumber, data]) => (
                <div
                  key={tableNumber}
                  className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-red-200 dark:border-red-700"
                >
                  <div className="flex justify-between items-center mb-1">
                    <div className="font-bold text-red-700 dark:text-red-300">
                      שולחן {tableNumber}
                    </div>
                    {data.waiterName && (
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {data.waiterName}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {data.allergies.map((allergy, index) => (
                      <span
                        key={index}
                        className="bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 px-2 py-1 rounded text-xs"
                      >
                        {allergy}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Main Dashboard Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Kitchen Totals */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            סיכום למטבח
          </h3>

          <div className="space-y-4">
            {kitchenTotals.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                אין הזמנות עדיין
              </div>
            ) : (
              kitchenTotals.map((dish) => (
                <div key={dish.dishId} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-2">
                    <div className="font-medium text-gray-900 dark:text-white">{dish.dishName}</div>
                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{dish.total}</div>
                  </div>

                  {dish.remaining !== undefined && (
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      נותרו: <span className="font-semibold">{dish.remaining}</span>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Active Waiters */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
              </svg>
              מלצרים ({waiterStatuses.length})
            </h3>
            <button
              onClick={() => {
                loadAvailableWaiters();
                setShowAddWaiterModal(true);
              }}
              className="bg-green-600 hover:bg-green-700 text-white text-sm px-3 py-1 rounded-lg transition-colors flex items-center gap-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              הוסף מלצר
            </button>
          </div>

          <div className="space-y-4">
            {waiterStatuses.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                אין מלצרים מחוברים
              </div>
            ) : (
              waiterStatuses.map((waiter) => (
                <div key={waiter.id} className={`border rounded-lg p-4 ${waiter.isActive ? 'border-green-200 dark:border-green-700 bg-green-50 dark:bg-green-900/20' : 'border-gray-200 dark:border-gray-600'}`}>
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${waiter.isActive ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                      <span className="font-medium text-gray-900 dark:text-white">{waiter.name}</span>
                    </div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {waiter.totalOrders} הזמנות
                    </span>
                  </div>

                  {waiter.assignedTables.length > 0 && (
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                      שולחנות: {waiter.assignedTables.join(', ')}
                    </div>
                  )}

                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                    נראה לאחרונה: {new Date(waiter.lastSeen).toLocaleTimeString('he-IL')}
                  </div>

                  {/* Waiter Management Actions */}
                  <div className="flex gap-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                    <button
                      onClick={() => setWaiterAction({ type: 'reset-pin', waiterId: waiter.id, waiterName: waiter.name })}
                      className="flex-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 text-xs px-2 py-1 rounded hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
                    >
                      איפוס PIN
                    </button>
                    {waiter.isActive && (
                      <button
                        onClick={() => setWaiterAction({ type: 'disconnect', waiterId: waiter.id, waiterName: waiter.name })}
                        className="flex-1 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 text-xs px-2 py-1 rounded hover:bg-red-200 dark:hover:bg-red-800 transition-colors"
                      >
                        נתק
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderCurrentView = () => {
    if (currentView === 'dishes') {
      return (
        <div>
          <div className="flex items-center mb-6 p-4">
            <button
              onClick={() => setCurrentView('dashboard')}
              className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              חזור ללוח הבקרה
            </button>
          </div>
          <DishManager />
        </div>
      );
    }

    if (currentView === 'waiters') {
      return (
        <div>
          <div className="flex items-center mb-6 p-4">
            <button
              onClick={() => setCurrentView('dashboard')}
              className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              חזור ללוח הבקרה
            </button>
          </div>
          <WaiterManager />
        </div>
      );
    }

    if (currentView === 'allergies') {
      return (
        <div>
          <div className="flex items-center mb-6 p-4">
            <button
              onClick={() => setCurrentView('dashboard')}
              className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              חזור ללוח הבקרה
            </button>
          </div>
          <AllergyManager />
        </div>
      );
    }

    if (currentView === 'create-event') {
      return (
        <EventCreator
          mode="create"
          onBack={() => setCurrentView('dashboard')}
          onComplete={() => {
            setCurrentView('saved-events');
          }}
        />
      );
    }

    if (currentView === 'edit-event' && editEventId) {
      return (
        <EventCreator
          mode="edit"
          eventId={editEventId}
          onBack={() => {
            setEditEventId(null);
            setCurrentView('saved-events');
          }}
          onComplete={() => {
            setEditEventId(null);
            loadSavedEvents();
            setCurrentView('saved-events');
          }}
        />
      );
    }

    if (currentView === 'saved-events') {
      return (
        <div className="max-w-4xl mx-auto p-3 sm:p-6">
          <div className="flex items-center mb-6">
            <button
              onClick={() => setCurrentView('dashboard')}
              className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              חזור ללוח הבקרה
            </button>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 sm:p-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">אירועים שמורים</h2>

            {isLoadingSavedEvents ? (
              <div className="text-center py-12">
                <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
                <p className="text-gray-500 dark:text-gray-400">טוען אירועים...</p>
              </div>
            ) : savedEvents.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 dark:text-gray-400 mb-4">אין אירועים שמורים</p>
                <button
                  onClick={() => setCurrentView('create-event')}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
                >
                  צור אירוע חדש
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {savedEvents.map(event => (
                  <div key={event.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {event.name}
                        </h3>
                        <div className="flex flex-wrap gap-3 mt-2 text-sm text-gray-600 dark:text-gray-300">
                          {event.scheduledFor && (
                            <span className="flex items-center gap-1">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              {new Date(event.scheduledFor).toLocaleDateString('he-IL')}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                            </svg>
                            {event.tableCount} שולחנות
                          </span>
                          <span className={`flex items-center gap-1 ${event.waiterCount === 0 ? 'text-yellow-600 dark:text-yellow-400' : ''}`}>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            {event.waiterCount} מלצרים
                            {event.waiterCount === 0 && ' (נדרש להוסיף)'}
                          </span>
                          <span className="flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                            {event.breaks?.length || 0} מנות
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleStartEvent(event.id)}
                          disabled={isStartingEvent || activeEvent !== null || event.waiterCount === 0}
                          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                            activeEvent !== null
                              ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 cursor-not-allowed'
                              : event.waiterCount === 0
                              ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 cursor-not-allowed'
                              : 'bg-green-600 hover:bg-green-700 text-white'
                          }`}
                          title={activeEvent !== null ? 'לא ניתן להתחיל אירוע בזמן שאירוע אחר פעיל' : event.waiterCount === 0 ? 'יש להקצות מלצרים לפני התחלת האירוע' : ''}
                        >
                          {isStartingEvent ? (
                            <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                          ) : (
                            'התחל'
                          )}
                        </button>
                        <button
                          onClick={() => handleEditEvent(event.id)}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                        >
                          עריכה
                        </button>
                        <button
                          onClick={() => handleDeleteDraftEvent(event.id)}
                          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors"
                        >
                          מחיקה
                        </button>
                      </div>
                    </div>
                    {activeEvent !== null && (
                      <p className="text-sm text-yellow-600 dark:text-yellow-400 mt-2">
                        לא ניתן להתחיל אירוע בזמן שאירוע אחר פעיל
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Start Event PINs Modal */}
          {startEventPins && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 text-center">
                  קודי גישה למלצרים
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 text-center">
                  שמור את הקודים הבאים ושתף אותם עם המלצרים
                </p>

                <div className="space-y-3 mb-6">
                  {startEventPins.map((waiter, index) => (
                    <div
                      key={index}
                      className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4"
                    >
                      <div className="flex justify-between items-center">
                        <p className="font-semibold text-gray-900 dark:text-white">
                          {waiter.name}
                        </p>
                        <div className="bg-blue-600 text-white px-4 py-2 rounded-lg text-xl font-mono font-bold">
                          {waiter.pin}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <button
                  onClick={async () => {
                    setStartEventPins(null);
                    await Promise.all([
                      loadActiveEvent(),
                      loadAdminData()
                    ]);
                    setCurrentView('live-event');
                  }}
                  className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-semibold transition-colors"
                >
                  הבנתי, המשך לאירוע
                </button>
              </div>
            </div>
          )}
        </div>
      );
    }

    if (currentView === 'live-event' && activeEvent) {
      return <ActiveEventView />;
    }

    if (currentView === 'settings') {
      return (
        <div className="max-w-4xl mx-auto p-6">
          <div className="flex items-center mb-6">
            <button
              onClick={() => setCurrentView('dashboard')}
              className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              חזור ללוח הבקרה
            </button>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">הגדרות</h2>

            <div className="space-y-6">
              {/* Change PIN */}
              <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">שינוי קוד PIN מנהל</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      קוד PIN חדש
                    </label>
                    <input
                      type="password"
                      maxLength={4}
                      pattern="[0-9]*"
                      inputMode="numeric"
                      value={newPin}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '');
                        setNewPin(value);
                        setPinChangeStatus(null);
                      }}
                      className="w-48 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-center text-2xl tracking-widest"
                      placeholder="••••"
                    />
                  </div>

                  {pinChangeStatus && (
                    <div className={`p-3 rounded-lg ${
                      pinChangeStatus.type === 'success'
                        ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                        : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                    }`}>
                      {pinChangeStatus.message}
                    </div>
                  )}

                  <button
                    onClick={handlePinChange}
                    disabled={isChangingPin || newPin.length !== 4}
                    className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
                  >
                    {isChangingPin && (
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    )}
                    עדכן PIN
                  </button>
                </div>
              </div>

              {/* Kitchen PIN */}
              <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">קוד PIN מטבח</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  קוד הגישה למסך המטבח מוגדר בהגדרות השרת
                </p>
              </div>

              {/* About */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">אודות</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Giggo - מערכת ניהול אירועים<br />
                  גרסה 1.0.0
                </p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (currentView === 'archive') {
      return (
        <div className="max-w-6xl mx-auto p-6">
          <div className="flex items-center mb-6">
            <button
              onClick={() => setCurrentView('dashboard')}
              className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              חזור ללוח הבקרה
            </button>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">ארכיון אירועים</h2>
              <button
                onClick={loadArchivedEvents}
                className="text-blue-600 dark:text-blue-400 hover:text-blue-700 flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                רענן
              </button>
            </div>

            {isLoadingArchive ? (
              <div className="text-center py-12">
                <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
                <p className="text-gray-500 dark:text-gray-400">טוען אירועים...</p>
              </div>
            ) : archivedEvents.length === 0 ? (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <svg className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                </svg>
                <p className="text-lg mb-2">אין אירועים בארכיון</p>
                <p className="text-sm">אירועים שהסתיימו יופיעו כאן</p>
              </div>
            ) : (
              <div className="space-y-4">
                {archivedEvents.map((event) => (
                  <div
                    key={event.id}
                    onClick={() => loadEventSummary(event.id)}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {event.name}
                        </h3>
                        <div className="text-sm text-gray-500 dark:text-gray-400 mt-1 space-y-1">
                          <p>
                            התחלה: {event.startedAt ? new Date(event.startedAt).toLocaleString('he-IL') : new Date(event.createdAt).toLocaleString('he-IL')}
                          </p>
                          {event.endedAt && (
                            <p>
                              סיום: {new Date(event.endedAt).toLocaleString('he-IL')}
                            </p>
                          )}
                          <p>שולחנות: {event.tableCount}</p>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <span className="inline-block bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 px-3 py-1 rounded-full text-sm">
                          הסתיים
                        </span>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteEvent(event.id); }}
                          disabled={isDeletingEvent}
                          className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 text-sm flex items-center gap-1 disabled:opacity-50"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          מחק
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      );
    }

    return <MenuView />;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <>
      <EmergencyAlertsBanner />
      <WaiterActionModal />
      <TableAssignmentModal />
      <TableActionModal />
      <AddTablesModal />
      <AddWaiterModal />
      <NewWaiterPinModal />
      {/* Message Modal - inlined to prevent focus loss from re-renders */}
      {showMessageModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 text-center">
              שלח הודעה
            </h3>

            {/* Message Text */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                תוכן ההודעה
              </label>
              <textarea
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                rows={3}
                placeholder="הקלד את ההודעה כאן..."
                dir="rtl"
              />
            </div>

            {/* Target Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                שלח ל:
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setMessageTarget('all')}
                  className={`p-3 rounded-lg border text-center transition-colors ${
                    messageTarget === 'all'
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300'
                      : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <div className="font-medium">כולם</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">מלצרים + מטבח</div>
                </button>
                <button
                  onClick={() => setMessageTarget('waiters')}
                  className={`p-3 rounded-lg border text-center transition-colors ${
                    messageTarget === 'waiters'
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300'
                      : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <div className="font-medium">מלצרים</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">כל המלצרים</div>
                </button>
                <button
                  onClick={() => setMessageTarget('kitchen')}
                  className={`p-3 rounded-lg border text-center transition-colors ${
                    messageTarget === 'kitchen'
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300'
                      : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <div className="font-medium">מטבח</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">תצוגת מטבח</div>
                </button>
                <button
                  onClick={() => setMessageTarget('specific')}
                  className={`p-3 rounded-lg border text-center transition-colors ${
                    messageTarget === 'specific'
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300'
                      : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <div className="font-medium">מלצר ספציפי</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">בחר מלצר</div>
                </button>
              </div>
            </div>

            {/* Specific Waiter Selection */}
            {messageTarget === 'specific' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  בחר מלצר
                </label>
                <select
                  value={specificWaiterId}
                  onChange={(e) => setSpecificWaiterId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">בחר מלצר...</option>
                  {waiterStatuses.map(waiter => (
                    <option key={waiter.id} value={waiter.id}>
                      {waiter.name} {waiter.isActive ? '(מחובר)' : '(מנותק)'}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowMessageModal(false);
                  setMessageText('');
                  setMessageTarget('all');
                  setSpecificWaiterId('');
                }}
                disabled={isSendingMessage}
                className="flex-1 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 text-gray-800 dark:text-white font-medium py-2 rounded-lg transition-colors"
              >
                ביטול
              </button>
              <button
                onClick={handleSendMessage}
                disabled={isSendingMessage || !messageText.trim() || (messageTarget === 'specific' && !specificWaiterId)}
                className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium py-2 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                {isSendingMessage && (
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                )}
                שלח
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Event Summary Modal */}
      {(selectedEventSummary || isLoadingEventSummary) && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {isLoadingEventSummary ? (
              <div className="text-center py-12">
                <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
                <p className="text-gray-500 dark:text-gray-400">טוען פרטי אירוע...</p>
              </div>
            ) : selectedEventSummary && (
              <>
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                      {selectedEventSummary.name}
                    </h3>
                    {selectedEventSummary.duration && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        משך: {selectedEventSummary.duration} שעות
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => setSelectedEventSummary(null)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Event Info */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400">התחלה</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {selectedEventSummary.startedAt ? new Date(selectedEventSummary.startedAt).toLocaleString('he-IL') : '-'}
                    </p>
                  </div>
                  <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400">סיום</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {selectedEventSummary.endedAt ? new Date(selectedEventSummary.endedAt).toLocaleString('he-IL') : '-'}
                    </p>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-4 gap-3 mb-6">
                  <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{selectedEventSummary.tableCount}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">שולחנות</p>
                  </div>
                  <div className="bg-purple-50 dark:bg-purple-900/30 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{selectedEventSummary.estimatedGuests || '-'}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">אורחים משוער</p>
                  </div>
                  <div className="bg-green-50 dark:bg-green-900/30 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-green-600 dark:text-green-400">{selectedEventSummary.totalGuests}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">אורחים בפועל</p>
                  </div>
                  <div className="bg-orange-50 dark:bg-orange-900/30 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">{selectedEventSummary.totalOrders}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">סה״כ מנות</p>
                  </div>
                </div>

                {/* Breaks */}
                {selectedEventSummary.breaks && selectedEventSummary.breaks.length > 0 && (
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">מהלכים</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedEventSummary.breaks.map((breakName: string, index: number) => (
                        <span key={index} className="bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 px-3 py-1 rounded-full text-sm">
                          {breakName}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Dish Totals */}
                {selectedEventSummary.dishTotals && selectedEventSummary.dishTotals.length > 0 && (
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">סיכום מנות</h4>
                    <div className="bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-gray-200 dark:bg-gray-600">
                          <tr>
                            <th className="px-4 py-2 text-right text-sm font-medium text-gray-700 dark:text-gray-300">מנה</th>
                            <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-300">כמות</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedEventSummary.dishTotals.map((dish: { dishName: string; total: number }, index: number) => (
                            <tr key={index} className="border-t border-gray-200 dark:border-gray-600">
                              <td className="px-4 py-2 text-gray-900 dark:text-white">{dish.dishName}</td>
                              <td className="px-4 py-2 text-left font-medium text-gray-900 dark:text-white">{dish.total}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Waiters */}
                {selectedEventSummary.waiters && selectedEventSummary.waiters.length > 0 && (
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">מלצרים</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedEventSummary.waiters.map((waiter: { id: string; name: string }) => (
                        <span key={waiter.id} className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-3 py-1 rounded-full text-sm">
                          {waiter.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Close Button */}
                <div className="flex justify-center">
                  <button
                    onClick={() => setSelectedEventSummary(null)}
                    className="bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 text-gray-800 dark:text-white font-medium px-6 py-2 rounded-lg transition-colors"
                  >
                    סגור
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      <div className={showEmergencyBanner && emergencyAlerts.length > 0 ? 'pt-16' : ''}>
        {renderCurrentView()}
      </div>
    </>
  );
};

export default AdminDashboard;
